import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { timeAgo } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, CheckCircle2, Circle, Clock } from "lucide-react";

const COLS = [
  { key: "open", label: "To Do", tint: "#94a3b8" },
  { key: "in-progress", label: "In Progress", tint: "#FFBA08" },
  { key: "done", label: "Done", tint: "#06D6A0" },
];
const PRIORITY = { high: "bg-red-100 text-red-700", medium: "bg-yellow-100 text-yellow-800", low: "bg-neutral-100 text-neutral-600" };

export default function Tasks() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ title: "", description: "", assigned_to: "", priority: "medium", due_date: today });

  const { data: tasks = [] } = useQuery({ queryKey: ["tasks"], queryFn: async () => (await api.get("/tasks")).data });
  const { data: staff = [] } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/auth/staff")).data });

  const add = async () => {
    if (!form.title) return toast.error("Title required");
    const st = staff.find((x) => x.id === form.assigned_to);
    await api.post("/tasks", { ...form, assigned_name: st?.name || "", status: "open" });
    toast.success("Task created"); setOpen(false);
    setForm({ title: "", description: "", assigned_to: "", priority: "medium", due_date: today });
    qc.invalidateQueries({ queryKey: ["tasks"] });
  };
  const move = async (t, status) => { await api.put(`/tasks/${t.id}`, { ...t, status }); qc.invalidateQueries({ queryKey: ["tasks"] }); };
  const del = async (t) => { await api.delete(`/tasks/${t.id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["tasks"] }); };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Tasks" subtitle="Assign, track & verify staff operational tasks">
        <Button onClick={() => setOpen(true)} data-testid="add-task-btn" className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white"><Plus className="w-4 h-4 mr-1" /> New Task</Button>
      </PageHeader>

      <div className="grid md:grid-cols-3 gap-4">
        {COLS.map((col) => (
          <div key={col.key} className="bg-white/50 rounded-xl border border-[var(--kul-border)] p-3">
            <div className="flex items-center gap-2 mb-3 px-1">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: col.tint }} />
              <h3 className="font-display font-700">{col.label}</h3>
              <span className="text-xs text-neutral-400">{tasks.filter((t) => t.status === col.key).length}</span>
            </div>
            <div className="space-y-2 min-h-[60px]">
              {tasks.filter((t) => t.status === col.key).map((t) => (
                <div key={t.id} className="kul-card p-3" data-testid={`task-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-sm">{t.title}</div>
                    <button onClick={() => del(t)} className="text-neutral-300 hover:text-[var(--kul-danger)]"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {t.description && <p className="text-xs text-neutral-500 mt-1">{t.description}</p>}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Badge className={`text-xs capitalize ${PRIORITY[t.priority]}`}>{t.priority}</Badge>
                    {t.assigned_name && <span className="text-xs text-neutral-500">{t.assigned_name}</span>}
                    {t.due_date && <span className="text-xs text-neutral-400 flex items-center gap-0.5"><Clock className="w-3 h-3" />{t.due_date}</span>}
                  </div>
                  <div className="flex gap-1 mt-2">
                    {col.key !== "open" && <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => move(t, col.key === "done" ? "in-progress" : "open")}>←</Button>}
                    {col.key !== "done" && <Button size="sm" className="h-7 text-xs flex-1 bg-[var(--kul-primary)] text-white" data-testid={`task-next-${t.id}`} onClick={() => move(t, col.key === "open" ? "in-progress" : "done")}>{col.key === "open" ? "Start" : "Done"}</Button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Assign to</Label><select value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm" data-testid="task-assign"><option value="">Unassigned</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              <div><Label>Priority</Label><select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm" data-testid="task-priority"><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
            </div>
            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={add} className="bg-[var(--kul-primary)] text-white" data-testid="save-task">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
